import { FC, Fragment, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useOutletContext } from 'react-router-dom'

import { Loading } from '../../global/Loading/Loading'
import { useCurrentTab } from '../../hooks/useCurrentTab'
import { MetricsIcon } from '../../icons/MetricsIcon'
import { PageSection } from '../../ui/v2/PageSection/PageSection'
import { PageSectionTabs, Tab } from '../../ui/v2/PageSection/PageSectionTabs'
import { useScrollToTop } from '../../utils/useScrollToTop'
import { useIBRoutes } from '../IntroducingBrokerRoutes'
import { useIntroducingBrokerById } from '../hooks/useIntroducingBrokerById'

export const IBRebatesPage: FC = () => {
  useScrollToTop()
  const { t } = useTranslation()
  const routes = useIBRoutes()
  const { data: introducingBroker, isLoading, forceRefresh } = useIntroducingBrokerById()
  const currentTab = useCurrentTab('rebates')

  const tabs = useMemo<Tab[]>(() => {
    const ibRoutes =
      routes.find((route) => route.path === 'introducing-broker' && route.condition)?.children || []

    const rebatesRoutes = ibRoutes.find((route) => route.path === 'rebates')?.children || []

    const nameByPath: Record<string, string> = {
      groups: t('IB.RebatesPage.Groups'),
      clients: t('IB.RebatesPage.Clients'),
      approvals: t('IB.RebatesPage.Approvals'),
      transactions: t('IB.RebatesPage.Transactions'),
    }

    const alertByPath: Record<string, boolean> = {
      approvals: !!introducingBroker?.notifications?.hasRebateApprovalsNotification,
      transactions: !!introducingBroker?.notifications?.hasRebateTransactionsNotification,
    }

    return rebatesRoutes
      ?.map((route) => {
        return {
          alert: alertByPath[route.path || ''] || false,
          label: route.path ? nameByPath[route.path] : route.path ?? '',
          path: getFullPath(route.path),
        }
      })
      .filter((tab) => !!tab.path && !!tab.label)
  }, [routes, t, introducingBroker?.notifications])

  const refreshIBNotifications = () => {
    if (
      introducingBroker?.notifications?.hasRebateApprovalsNotification ||
      introducingBroker?.notifications?.hasRebateTransactionsNotification
    ) {
      forceRefresh(true)
    }
  }

  return (
    <Fragment>
      <Loading isLoading={isLoading} showLoadingIcon>
        <PageSection
          icon={MetricsIcon}
          title={t('IB.RebatesPage.Metrics')}
          tabData={{ tabs, activePath: getFullPath(currentTab) }}
        />
        <Outlet context={{ refreshIBNotifications }} />
      </Loading>
    </Fragment>
  )
}

const getFullPath = (path?: string): string => {
  if (!path) {
    return '/dashboard/introducing-broker/rebates'
  }
  return `/dashboard/introducing-broker/rebates/${path}`
}

interface IBRebatesPageOutletContext {
  refreshIBNotifications: () => void
}

export const useIBRebatesPageOutletContext = (): IBRebatesPageOutletContext => {
  return useOutletContext<IBRebatesPageOutletContext>()
}
